import { useRouter } from 'next/router'
import Image from 'next/legacy/image'
import { useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import cn from 'classnames'
import Script from 'next/script'
// Components
import Loader from '@/components/newLoader/Loader.js'
import Header from '@/components/newHeader/Header'
import Button from '@/components/newButton/Button'
import Questions from '@/components/blog/Questions'
import CarouselPuppies from '@/components/puppy-travel/carouselPuppies'
import Footer from '@/components/newFooter/newFooter'
import { comments } from '@/components/reviews/customer-opinions'
import BasicHeadSeo from '@/components/basicHeadSeo'
// Styles
import useMediaQuery from '@/utils/publicApi'
import styles from './index.module.scss'
// Images
import mainImage from '../../public/reviews/image-header.png'
import mainImageMobile from '../../public/reviews/image-header-mobile.png'

const Card = ({ stars, title, content, name, date, icon, iconGoogle, key }) => {
  const [showContent, setShowContent] = useState(false)
  const shortContent =
    content.length > 230 ? content.slice(0, 230) + '...' : content
  const isTablet = useMediaQuery('(max-width:1270px)')
  return (
    <div className={styles.cardContainer} key={key}>
      <img className={styles.stars} src={stars} alt="icon five starts" />
      <h4>{title}</h4>
      {isTablet ? (
        showContent ? (
          <p>{content}</p>
        ) : (
          <p>{shortContent}</p>
        )
      ) : (
        <p>{content}</p>
      )}
      {content.length > 230 ? (
        isTablet && showContent ? (
          <div
            className={styles.btnView}
            onClick={() => setShowContent(!showContent)}
          >
            View Less
          </div>
        ) : (
          <div
            className={styles.btnView}
            onClick={() => setShowContent(!showContent)}
          >
            View More
          </div>
        )
      ) : null}
      <span>
        <b>{name}</b>
        {date}
      </span>
      <img
        className={cn({ [styles.icon]: icon, [styles.iconGoogle]: iconGoogle })}
        src={icon || iconGoogle}
        alt="icon"
      />
    </div>
  )
}

export default function Reviews() {
  const router = useRouter()
  const [showAll, setShowAll] = useState(false)

  const group1 = showAll
    ? [comments[0], comments[3], comments[6], comments[9], comments[12]]
    : [comments[0], comments[3], comments[6], comments[9]]
  const group2 = showAll
    ? [
        comments[1],
        comments[4],
        comments[7],
        comments[10],
        comments[13],
        comments[15],
      ]
    : [comments[1], comments[4], comments[7]]
  const group3 = showAll
    ? [comments[2], comments[5], comments[8], comments[11], comments[14]]
    : [comments[2], comments[5], comments[8], comments[11]]

  const ref = useRef(null)
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)

  const goNext = async () => {
    if (ref.current !== null && ref.current.swiper !== null) {
      await ref.current.swiper.slideNext()
      setIsBeginning(ref.current?.swiper?.isBeginning)
      setIsEnd(ref.current?.swiper?.isEnd)
    }
  }

  const goPrev = async () => {
    if (ref.current !== null && ref.current.swiper !== null) {
      await ref.current.swiper.slidePrev()
      setIsBeginning(ref.current?.swiper?.isBeginning)
      setIsEnd(ref.current?.swiper?.isEnd)
    }
  }

  return (
    <>
      <BasicHeadSeo
        title="Customer Reviews | Mawoo Pets"
        description="Discover what real customers have to say about Mawoo Pets! See all reviews and shop with confidence!"
        url={`${process.env.NEXT_PUBLIC_ROOT_URL}/reviews`}
        image={`${process.env.NEXT_PUBLIC_ROOT_URL}/mawoo_social_1600x906.png`}
        content={
          process.env.NEXT_PUBLIC_ROOT_URL ===
          'https://qa-front.friendapuppy.com'
            ? 'noindex, nofollow'
            : 'index, follow'
        }
      />
      <Script
        type="text/javascript"
        src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
        async
      />
      <main className={styles.main}>
        {router.isFallback && (
          <div className={styles.loader}>
            <Loader theme="tall" />
          </div>
        )}

        <Header />
        <div className={styles.content}>
          <section className={styles.header}>
            <picture className={styles.imageContainer}>
              <span className={styles.imageDesktop}>
                <Image
                  src={mainImage}
                  layout="intrinsic"
                  alt="article image"
                  loading="lazy"
                  placeholder="blur"
                  blurDataURL={`/_next/image?url=${mainImage}&w=16&q=1`}
                />
                <img
                  src="/reviews/heart-icon.svg"
                  alt="heart icon"
                  className={styles.heart1}
                />
                <img
                  src="/reviews/heart-icon-2.svg"
                  alt="heart icon"
                  className={styles.heart2}
                />
                <img
                  src="/reviews/heart-icon.svg"
                  alt="heart icon"
                  className={styles.heart3}
                />
              </span>

              <span className={styles.imageMobile}>
                <Image
                  src={mainImageMobile}
                  layout="responsive"
                  width={1074}
                  height={500}
                  quality={100}
                  objectFit="fill"
                  alt="article image"
                  loading="lazy"
                  placeholder="blur"
                  blurDataURL={`/_next/image?url=${mainImageMobile}&w=16&q=1`}
                />
              </span>
            </picture>
            <div className={styles.infoContainer}>
              <h1 className={styles.title}>
                Read real reviews left by <br />
                our customers!
              </h1>
              <p className={styles.text}>
                Our flight nannies delivering pawfection to every state! We love
                seeing our puppies find their forever homes and get lots of love
                and snuggles! <br />
                <br />
                Tag your photo on instagram with <b>#mawoopets</b> for a chance
                to be featured!
              </p>
              <div className={styles.squares}>
                <div className={styles.square}>
                  <div className={styles.number}>+150</div>
                  <p className={styles.text}>
                    {' '}
                    Reviews on Trustpilot <br />
                    and Google
                  </p>
                </div>
                <div className={styles.square}>
                  <div className={styles.title}>Rated Excellent</div>
                  <div className={styles.stars} />
                  <div className={styles.icon} />
                </div>
              </div>
            </div>
          </section>

          <section className={styles.carousel}>
            <h2 className={styles.title}>
              Our adorable pups with their furever families!
            </h2>
          </section>

          <div className={styles.containerCarousel}>
            <CarouselPuppies />
          </div>
          <section className={styles.customerReviews}>
            <h1 className={styles.title}>Don't take our word for it!</h1>
            <h1 className={styles.subtitle}>
              See what our customer have to say!
            </h1>

            <div className={styles.cardReview}>
              <div className={styles.reviewColumn}>
                {group1?.map((elem, index) => (
                  <Card
                    key={index}
                    stars={elem.stars}
                    title={elem.title}
                    content={elem.text}
                    name={elem.name}
                    date={elem.date}
                    icon={elem.logo}
                    iconGoogle={elem.logoGoogle}
                  />
                ))}
              </div>
              <div className={styles.reviewColumn}>
                {group2?.map((elem, index) => (
                  <Card
                    key={index}
                    stars={elem.stars}
                    title={elem.title}
                    content={elem.text}
                    name={elem.name}
                    date={elem.date}
                    icon={elem.logo}
                    iconGoogle={elem.logoGoogle}
                  />
                ))}
              </div>
              <div className={styles.reviewColumn}>
                {group3?.map((elem, index) => (
                  <Card
                    key={index}
                    stars={elem.stars}
                    title={elem.title}
                    content={elem.text}
                    name={elem.name}
                    date={elem.date}
                    icon={elem.logo}
                    iconGoogle={elem.logoGoogle}
                  />
                ))}
              </div>
            </div>
            <div className={styles.viewMore}>
              {' '}
              <Button
                title={showAll ? 'View less' : 'View more'}
                theme={['black', 'medium']}
                rightImage={
                  showAll
                    ? '/search/arrow-top-black.svg'
                    : '/search/arrow-down-black.svg'
                }
                altImg={showAll ? 'Black up arrow' : 'Black down arrow'}
                onClick={() => setShowAll((curr) => !curr)}
              />
            </div>

            <div className={styles.swiper}>
              <button
                className={cn({
                  [styles.swiperButton]: true,
                  [styles.swiperPrev]: !isBeginning,
                  [styles.disabledPrev]: isBeginning,
                })}
                onClick={goPrev}
              />
              <button
                className={cn({
                  [styles.swiperButton]: true,
                  [styles.swiperNext]: !isEnd,
                  [styles.disabledNext]: isEnd,
                })}
                onClick={goNext}
              />
              <Swiper
                speed={1000}
                initialSlide={0}
                grabCursor
                pagination={{ clickable: true }}
                breakpoints={{
                  1020: {
                    slidesPerView: 3,
                    spaceBetween: 10,
                  },
                  860: {
                    slidesPerView: 2,
                    spaceBetween: -40,
                  },
                  540: {
                    slidesPerView: 2,
                    spaceBetween: 10,
                  },
                  360: {
                    slidesPerView: 1,
                    spaceBetween: 10,
                  },
                }}
                ref={ref}
              >
                {comments &&
                  comments?.map((elem, index) => (
                    <SwiperSlide key={index}>
                      <div className={styles.cardSwipe}>
                        <Card
                          key={index}
                          stars={elem.stars}
                          title={elem.title}
                          content={elem.text}
                          name={elem.name}
                          date={elem.date}
                          icon={elem.logo}
                          iconGoogle={elem.logoGoogle}
                        />
                      </div>
                    </SwiperSlide>
                  ))}
              </Swiper>
            </div>
          </section>
          <div className={styles.line} />
          <Questions />
        </div>
        <Footer />
      </main>
    </>
  )
}
